import React, { useState, useEffect, useCallback, useRef } from "react";
import "../styles/Blog.css";
import { FaEye, FaHeart, FaCalendar, FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa"; 
import { Link, useLocation } from "react-router-dom"; 

const API = "https://backend.afrivisionconsultants.com";
// const API_URL = "http://localhost:5000";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All"); 
  const [search, setSearch] = useState("");

  // --- CATEGORY LIMIT STATE ---
  const [showAllCategories, setShowAllCategories] = useState(false);
  const MAX_VISIBLE_CATEGORIES = 3; // Change this number to show more/less before the "More" button appears

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const blogTopRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");
    
    if (categoryParam) {
      setActiveCategory(categoryParam);
    } else {
      setActiveCategory("All");
    }

    setTimeout(() => {
      if (blogTopRef.current) {
        blogTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
    
  }, [location.search]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/public/blog/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to load categories", err);
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchPosts = useCallback(async () => {
    try {
      let url = `${API}/public/blog/posts?`;
      
      if (activeCategory === "Popular") {
        url += `filter=popular&`; 
      } else if (activeCategory === "Recent" || activeCategory === "All") {
        url += `filter=recent&`; 
      } else {
        url += `category=${activeCategory}&`;
      }
      
      if (search) url += `search=${search}`;
      
      const res = await fetch(url);
      const data = await res.json();
      
      // SAFETY CHECK: Ensure data is an array before setting state
      if (Array.isArray(data)) {
        setPosts(data);
      } else if (data && Array.isArray(data.posts)) {
        // Just in case backend wraps it in a 'posts' object
        setPosts(data.posts);
      } else {
        console.error("Expected an array of posts, got:", data);
        setPosts([]); // Fallback to empty array
      }
      
    } catch (err) {
      console.error("Failed to load posts", err);
      setPosts([]); // Fallback to empty array on error
    }
  }, [activeCategory, search]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Determine which categories to show
  const visibleCategories = showAllCategories 
    ? categories 
    : categories.slice(0, MAX_VISIBLE_CATEGORIES);

  // PAGINATION CALCULATIONS WITH SAFETY CHECKS
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  
  // Create a safe array just to guarantee .slice() and .length never crash
  const safePosts = Array.isArray(posts) ? posts : [];
  
  const currentPosts = safePosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(safePosts.length / postsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (blogTopRef.current) {
      blogTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section className="blog-section" ref={blogTopRef}>
      <div className="blog-header">
        <h2>Latest Insights</h2>
      </div>

      <div className="blog-toolbar">
        {/* Updated Category Filter Wrapper */}
        <div className="category-filter">
          <button 
            className={activeCategory === "All" ? "active" : ""} 
            onClick={() => setActiveCategory("All")}
          >
            All
          </button>
          <button 
            className={activeCategory === "Recent" ? "active" : ""} 
            onClick={() => setActiveCategory("Recent")}
          >
            Recent
          </button>
          <button 
            className={activeCategory === "Popular" ? "active" : ""} 
            onClick={() => setActiveCategory("Popular")}
          >
            Popular
          </button>
          
          {categories.length > 0 && <span className="filter-divider">|</span>}
          
          {visibleCategories.map(c => (
            <button 
              key={c.id} 
              className={activeCategory === c.name ? "active" : ""} 
              onClick={() => setActiveCategory(c.name)} 
            >
              {c.name}
            </button>
          ))}

          {/* "+ More" Toggle Button */}
          {categories.length > MAX_VISIBLE_CATEGORIES && (
            <button 
              className="more-categories-btn"
              onClick={() => setShowAllCategories(!showAllCategories)}
            >
              {showAllCategories ? "Show Less" : `+${categories.length - MAX_VISIBLE_CATEGORIES} More`}
            </button>
          )}
        </div>

        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input 
              className="search-bar" 
              placeholder="Search topics..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
          />
        </div>
      </div>

      <div className="posts-grid">
        {currentPosts.length > 0 ? (
          currentPosts.map(post => (
            <div key={post.id} className="post-card">
              {post.image && (
                <img src={`${API}/${post.image}`} alt={post.title} className="post-thumb" />
              )}
              <div className="post-body">
                  <h3>{post.title}</h3>
                  <p className="post-excerpt">
                      {post.excerpt ? post.excerpt : "No summary available."}
                  </p>
                  {post.keywords && (
                    <small className="post-tags">
                        {post.keywords.split(',').slice(0,2).join(', ')}
                    </small>
                  )}
                  <div className="post-footer">
                      <span className="post-date">
                        <FaCalendar style={{marginRight:5}}/>
                        {new Date(post.scheduledAt).toLocaleDateString()}
                      </span>
                      {activeCategory === "Popular" && (
                         <div style={{fontSize:"0.8rem", color:"#777", display:"flex", gap:"10px"}}>
                             <span><FaEye/> {post.views}</span>
                             <span><FaHeart/> {post.likes || 0}</span>
                         </div>
                      )}
                      <Link 
                        to={`/blog/${post.slug}`} 
                        className="read-more-btn"
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Read More
                      </Link>
                  </div>
              </div>
            </div>
          ))
        ) : (
          <div style={{gridColumn: "1 / -1", textAlign: "center", padding: "40px", color: "#666"}}>
            <p>No posts found for "{activeCategory}".</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination-container">
          <button 
            className="pagination-btn" 
            disabled={currentPage === 1}
            onClick={() => paginate(currentPage - 1)}
          >
            <FaChevronLeft /> Prev
          </button>
          
          <div className="pagination-numbers">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                className={`pagination-number ${currentPage === index + 1 ? "active" : ""}`}
                onClick={() => paginate(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <button 
            className="pagination-btn" 
            disabled={currentPage === totalPages}
            onClick={() => paginate(currentPage + 1)}
          >
            Next <FaChevronRight />
          </button>
        </div>
      )}
    </section>
  );
};

export default Blog;